'use client';

import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/context/LanguageContext';

type ConsentState = {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
};

const COOKIE_KEY = 'nexavi_cookie_consent';

export default function CookieConsentBanner() {
  const { t } = useLanguage();
  const [visible, setVisible] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [consent, setConsent] = useState<ConsentState>({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_KEY);
    if (!stored) {
      setVisible(true);
    }
  }, []);

  const saveConsent = (state: ConsentState) => {
    localStorage.setItem(COOKIE_KEY, JSON.stringify({ ...state, necessary: true, timestamp: Date.now() }));
    setVisible(false);
  };

  const acceptAll = () => saveConsent({ necessary: true, analytics: true, marketing: true });
  const acceptNecessary = () => saveConsent({ necessary: true, analytics: false, marketing: false });
  const saveCustom = () => saveConsent(consent);

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-4 sm:p-6">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Banner */}
      <div className="relative w-full max-w-lg bg-charcoal border border-gold/20 rounded-2xl shadow-gold-glow overflow-hidden">
        {/* Gold top accent */}
        <div className="h-0.5 w-full bg-gold-gradient" />

        <div className="p-6 sm:p-8">
          {/* Header */}
          <div className="flex items-start gap-3 mb-4">
            <span className="text-2xl mt-0.5">🍪</span>
            <div>
              <h2 className="text-monitor font-sans font-semibold text-lg leading-tight">
                {t('cookies.title')}
              </h2>
              <p className="text-monitor/60 text-sm mt-1 leading-relaxed">
                {t('cookies.description')}
              </p>
            </div>
          </div>

          {/* Custom options panel */}
          {showCustom && (
            <div className="mb-5 space-y-3 border border-gold/10 rounded-xl p-4 bg-blackout/50">
              {/* Necessary — always on */}
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-monitor text-sm font-medium">{t('cookies.necessary')}</p>
                  <p className="text-monitor/50 text-xs mt-0.5">{t('cookies.necessaryDesc')}</p>
                </div>
                <div className="flex-shrink-0">
                  <div className="w-10 h-5 rounded-full bg-gold/40 flex items-center justify-end px-0.5 cursor-not-allowed">
                    <div className="w-4 h-4 rounded-full bg-gold" />
                  </div>
                </div>
              </div>

              {/* Analytics */}
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-monitor text-sm font-medium">{t('cookies.analytics')}</p>
                  <p className="text-monitor/50 text-xs mt-0.5">{t('cookies.analyticsDesc')}</p>
                </div>
                <button
                  onClick={() => setConsent(c => ({ ...c, analytics: !c.analytics }))}
                  className={`flex-shrink-0 w-10 h-5 rounded-full transition-colors duration-200 flex items-center px-0.5 ${
                    consent.analytics ? 'bg-gold justify-end' : 'bg-monitor/20 justify-start'
                  }`}
                  aria-label="Toggle analytics cookies"
                >
                  <div className="w-4 h-4 rounded-full bg-white shadow" />
                </button>
              </div>

              {/* Marketing */}
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-monitor text-sm font-medium">{t('cookies.marketing')}</p>
                  <p className="text-monitor/50 text-xs mt-0.5">{t('cookies.marketingDesc')}</p>
                </div>
                <button
                  onClick={() => setConsent(c => ({ ...c, marketing: !c.marketing }))}
                  className={`flex-shrink-0 w-10 h-5 rounded-full transition-colors duration-200 flex items-center px-0.5 ${
                    consent.marketing ? 'bg-gold justify-end' : 'bg-monitor/20 justify-start'
                  }`}
                  aria-label="Toggle marketing cookies"
                >
                  <div className="w-4 h-4 rounded-full bg-white shadow" />
                </button>
              </div>
            </div>
          )}

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={acceptAll}
              className="flex-1 bg-gold hover:bg-gold-dim text-blackout font-semibold text-sm py-2.5 px-4 rounded-lg transition-colors duration-200"
            >
              {t('cookies.acceptAll')}
            </button>
            <button
              onClick={acceptNecessary}
              className="flex-1 border border-gold/30 hover:border-gold/60 text-monitor/80 hover:text-monitor font-medium text-sm py-2.5 px-4 rounded-lg transition-colors duration-200"
            >
              {t('cookies.necessaryOnly')}
            </button>
            {!showCustom ? (
              <button
                onClick={() => setShowCustom(true)}
                className="flex-1 border border-monitor/10 hover:border-monitor/30 text-monitor/50 hover:text-monitor/80 font-medium text-sm py-2.5 px-4 rounded-lg transition-colors duration-200"
              >
                {t('cookies.customize')}
              </button>
            ) : (
              <button
                onClick={saveCustom}
                className="flex-1 border border-gold/30 hover:border-gold/60 text-gold font-medium text-sm py-2.5 px-4 rounded-lg transition-colors duration-200"
              >
                {t('cookies.saveSelection')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
